import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward, ArrowBack } from '@material-ui/icons';
import CategoriesTable from 'components/Dashboard/Categories/CategoriesTable';
import Loading from 'components/Basic/Loading';
import CategoriesViewModal from 'components/Dashboard/Categories/CategoriesViewModal';
import CategoriesFilter from 'components/Dashboard/Categories/CategoriesFilter';
import { NavLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetCategoriesListQuery } from 'core/categories/CategoriesService';
import { setCategoriesListData } from 'core/categories/CategoriesSlice';

const Categories: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isShowDetailModal, setShowDetailModal] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [current, setCurrent] = useState(1);
  const [sortName, setSortName] = useState('');
  const [sortId, setSortId] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  /* const [option, setOption] = useState(); */

  const dispatch = useAppDispatch();
  const [getCategoriesList] = useLazyGetCategoriesListQuery();

  const { list } = useAppSelector(state => state.categoriesReducer);

  useEffect(() => {
    getInitialData();
    setSearchText('');
    /* setOption(); */
  }, []);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  const getInitialData = async () => {
    setIsLoading(true);
    try {
      const queryString = buildQueryString({
        search: '',
        page: 1,
        size: pageSize,
        count: true,
        filter: 'omit',
      });
      const categoriesList = await getCategoriesList({ queryString }).unwrap();
      dispatch(setCategoriesListData({ list: categoriesList }));
    } catch (error) {
      console.log(' DEV!!! Error fetching categories list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = async (page: number, size: number) => {
    setIsLoading(true);
    setPageSize(size);
    try {
      const queryString = buildQueryString({
        search: searchText.trim(),
        page: page,
        size: size,
        count: true,
        sort_by_id: sortId,
        sort_by_name: sortName,
      });
      const categoriesList = await getCategoriesList({ queryString }).unwrap();
      dispatch(setCategoriesListData({ list: categoriesList }));
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setSearchText(searchTextValue);
    if (e.keyCode === 13) {
      setCurrent(1);
      try {
        const queryString = buildQueryString({
          search: e.target.value.trim(),
          page: 1,
          size: pageSize,
          count: true,
          sort_by_id: sortId,
          sort_by_name: sortName,
        });
        const categoriesList = await getCategoriesList({ queryString }).unwrap();
        dispatch(setCategoriesListData({ list: categoriesList }));
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleSort = (value: string) => {
    /* setOption(value); */
    if (value[0] === 'I') {
      setSortId(value.substring(1));
      setSortName('');
    } else if (value[0] === 'N') {
      setSortName(value.substring(1));
      setSortId('');
    }
  };

  const onApplyFilter = async () => {
    setIsLoading(true);
    setCurrent(1);
    try {
      const queryString = buildQueryString({
        search: searchText.trim(),
        page: 1,
        size: pageSize,
        count: true,
        sort_by_id: sortId,
        sort_by_name: sortName,
      });
      const categoriesList = await getCategoriesList({ queryString }).unwrap();
      dispatch(setCategoriesListData({ list: categoriesList }));
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  return (
    <div className="configure_tab_header_cat">
      <div className="header_title">
        <NavLink to="/dashboard/deals/offers">
          <div className="back_btn">
            <ArrowBack />
          </div>
        </NavLink>
        <FormattedMessage id="Configure offer categories" />
      </div>
      <div className="btn_wrapper">
        <Button className="add_btn" onClick={() => setShowDetailModal(true)}>
          <FormattedMessage id="Add new categories" />
          <ArrowForward />
        </Button>
      </div>
      <CategoriesFilter
        searchText={searchText}
        handleSearch={handleSearch}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
        /* option={option} */
      />
      <CategoriesTable
        categoriesData={list.categories || []}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.totalCount}
        setCurrent={setCurrent}
        current={current}
        setPageSize={setPageSize}
        pageSize={pageSize}
        offerModal={false}
      />
      <CategoriesViewModal
        isOpenModal={isShowDetailModal}
        onCloseModal={() => setShowDetailModal(false)}
        setPageSize={setPageSize}
        categoriesInfo={null}
        /* setOption={setOption} */
      />
      {isLoading && <Loading visible={isLoading} />}
    </div>
  );
};

export default Categories;

const buildQueryString = ({
  search = '',
  page = 1,
  size = 10,
  count = true,
  sort_by_name = '',
  sort_by_id = 'desc',
  filter = '',
}) => {
  const params = [
    `search=${encodeURIComponent(search)}`,
    `page=${page}`,
    `size=${size}`,
    `count=${count}`,
    `filter=${filter}`,
  ];

  if (sort_by_id) {
    params.push(`sort[id]=${sort_by_id}`);
  }

  if (sort_by_name) {
    params.push(`sort[name]=${sort_by_name}`);
  }

  return params.join('&');
};
